'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { HateoasTarget, MemoryRegistry, ToolsRegistry } from '@repo/contracts';

import { FrontActionHandler, FrontActionRegistration } from '@/utils/chatbot/front-actions';

interface AIContextValue {
    memory: MemoryRegistry;
    setMemoryData: (key: string, data: Record<string, unknown>) => void;
    removeMemoryData: (key: string) => void;
    tools: ToolsRegistry;
    getTools: () => ToolsRegistry;
    setToolsData: (key: string, data: Record<string, HateoasTarget>) => void;
    removeToolsData: (key: string) => void
    registerHandler: (id: string, fn: FrontActionHandler) => void;
    unregisterHandler: (id: string) => void;
    executeFrontAction: (id: string, args: Record<string, unknown>) => Promise<{ handled: boolean; error?: string }>;
}

export const AIContext = createContext<AIContextValue | null>(null);

export function AIProvider({ children }: { children: ReactNode }) {
    const [memory, setMemory] = useState<MemoryRegistry>({});
    const [tools, setTools] = useState<ToolsRegistry>({});
    const toolsRef = useRef<ToolsRegistry>({});
    const handlers = useRef<Map<string, FrontActionHandler>>(new Map());

    // Memory
    const setMemoryData = useCallback((key: string, data: Record<string, unknown>) => {
        setMemory((prev) => {
            if (JSON.stringify(prev[key]) === JSON.stringify(data)) return prev;
            return { ...prev, [key]: data };
        });
    }, []);

    const removeMemoryData = useCallback((key: string) => {
        setMemory((prev) => {
            if (!(key in prev)) return prev;
            const newRegistry = { ...prev };
            delete newRegistry[key];
            return newRegistry;
        });
    }, []);

    // Tools
    const setToolsData = useCallback((key: string, data: Record<string, HateoasTarget>) => {
        const current = toolsRef.current[key];
        if (JSON.stringify(current) === JSON.stringify(data)) return;
        
        const newTools = { ...toolsRef.current, [key]: data };
        toolsRef.current = newTools;
        setTools(newTools);
    }, []);

    const removeToolsData = useCallback((key: string) => {
        if (!(key in toolsRef.current)) return;
        const newTools = { ...toolsRef.current };
        delete newTools[key];
        toolsRef.current = newTools;
        setTools(newTools);
    }, []);

    // Handlers front
    const registerHandler = useCallback((id: string, fn: FrontActionHandler) => {
        handlers.current.set(id, fn);
    }, []);
    
    const unregisterHandler = useCallback((id: string) => {
        handlers.current.delete(id);
    }, []);
    
    const executeFrontAction = useCallback(
        async (id: string, args: Record<string, unknown>): Promise<{ handled: boolean; error?: string }> => {
            const handler = handlers.current.get(id);
            if (!handler) return { handled: false };
        
            try {
                await handler(args);
                return { handled: true };
            } catch (err) {
                const error =
                err instanceof Error ? err.message : `Erreur lors de l'exécution de "${id}"`;
                return { handled: true, error };
            }
        }, [],
    );


    const getTools = useCallback(() => toolsRef.current, []);

    const value = useMemo(() => ({
        memory,
        setMemoryData,
        removeMemoryData,
        tools,
        getTools,
        setToolsData,
        removeToolsData,
        registerHandler,
        unregisterHandler,
        executeFrontAction,
    }), [memory, setMemoryData, removeMemoryData, tools, getTools, setToolsData, removeToolsData, registerHandler, unregisterHandler, executeFrontAction]);

    return (
        <AIContext.Provider value={value}>
            {children}
        </AIContext.Provider>
    );
}

export function useAIContext(): AIContextValue {
    const ctx = useContext(AIContext);
    if (!ctx) throw new Error('useAIContext must be used inside AIProvider');
    return ctx;
}

export function useAIStateBinding(id: string, memory: Record<string, unknown> | undefined, tools: Record<string, HateoasTarget> | undefined) {
    const context = useContext(AIContext);
    if (!context) throw new Error('useAIStateBinding doit être dans AIProvider');

    // Met à jour le registre quand la donnée change
    useEffect(() => {
        if (memory !== undefined) context.setMemoryData(id, memory);
        if (tools !== undefined) context.setToolsData(id, tools);
    }, [id, memory, tools, context.setMemoryData, context.setToolsData]); 

    // Nettoie le registre UNIQUEMENT quand le composant est détruit (changement de page)
    useEffect(() => {
        return () => {
            context.removeMemoryData(id);
            context.removeToolsData(id);
        };
    }, [id, context.removeMemoryData, context.removeToolsData]);
}

export function useRegisterFrontActions(
    ownerId: string,
    actions: FrontActionRegistration[],
): void {
    const ctx = useContext(AIContext);
    if (!ctx) throw new Error('useRegisterFrontActions doit être dans AIProvider');
    
    // Sérialise uniquement les définitions (sans les handlers) pour la comparaison
    const definitionsString = JSON.stringify(
        actions.map(({ execute: _, ...def }) => def),
    );
    
    // Synchronise les définitions dans `tools`
    useEffect(() => {
        const toolEntries = Object.fromEntries(
            actions.map((action) => [
                action.id,
                {
                    href: `__front__/${action.id}`,
                    method: 'POST' as const,
                    label: action.label,
                    tool: {
                        name: action.id,
                        description: action.description,
                        parameters: { type: action.parameters?.type || 'object', properties: action.parameters?.properties || {} },
                    },
                },
            ]),
        );
    
        ctx.setToolsData(`__front__:${ownerId}`, toolEntries);
    
        return () => {
            ctx.removeToolsData(`__front__:${ownerId}`);
        };
    // definitionsString capture le contenu sérialisé
    }, [ownerId, definitionsString, ctx.setToolsData, ctx.removeToolsData]);
    
    // Synchronise les handlers dans le Map
    useEffect(() => {
        for (const action of actions) {
            ctx.registerHandler(action.id, action.execute);
        }
        return () => {
            for (const action of actions) {
                ctx.unregisterHandler(action.id);
            }
        };
    }, [ownerId, definitionsString, ctx.registerHandler, ctx.unregisterHandler]);
}